/**
 * useInvoiceListUrlState Hook
 * 
 * Encapsulates URL state management for the invoice list.
 * Handles deep links by persisting pagination, sorting, and filters in the URL.
 * 
 * This follows SRP by extracting URL state logic from the InvoicesList component.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Customer } from 'types';
import { SortColumn, SortDirection, isValidSortColumn, isValidSortDirection } from '../../utils/invoiceUtils';
import { FilterState, initialFilters, parseFiltersFromUrl } from './filterUtils';

// Default page size
const DEFAULT_PAGE_SIZE = 20;

/**
 * Build URL search params from current state
 */
const buildSearchParams = (
  page: number,
  perPage: number,
  sortColumn: SortColumn,
  sortDirection: SortDirection,
  filters: FilterState
): URLSearchParams => {
  const params = new URLSearchParams();
  
  // Pagination (only if not default)
  if (page !== 1) params.set('page', String(page));
  if (perPage !== DEFAULT_PAGE_SIZE) params.set('perPage', String(perPage));
  
  // Sorting (only if not default)
  if (sortColumn !== 'id' || sortDirection !== 'desc') {
    params.set('sort', sortColumn);
    params.set('dir', sortDirection);
  }
  
  // Filters
  if (filters.customer) {
    params.set('customerId', String(filters.customer.id));
    params.set('customerName', `${filters.customer.first_name} ${filters.customer.last_name}`);
  }
  if (filters.status !== 'all') params.set('status', filters.status);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.deadlineFrom) params.set('deadlineFrom', filters.deadlineFrom);
  if (filters.deadlineTo) params.set('deadlineTo', filters.deadlineTo);
  
  return params;
};

/**
 * Parse customer from URL params
 */
const parseCustomerFromUrl = (searchParams: URLSearchParams): Customer | null => {
  const customerId = searchParams.get('customerId');
  const customerName = searchParams.get('customerName');
  
  if (!customerId || !customerName) return null;
  
  const [firstName, ...lastNameParts] = customerName.split(' ');
  return {
    id: parseInt(customerId, 10),
    first_name: firstName,
    last_name: lastNameParts.join(' '),
    address: '',
    zip_code: '',
    city: '',
    country: '',
    country_code: '',
  };
};

export interface UseInvoiceListUrlStateResult {
  // Pagination
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setTotals: (totalPages: number, totalCount: number) => void;
  
  // Sorting
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  handleSort: (column: SortColumn) => void;
  
  // Filters
  filters: FilterState;
  appliedFilters: FilterState;
  isFilterExpanded: boolean;
  setFilters: (filters: FilterState) => void;
  setIsFilterExpanded: (expanded: boolean) => void;
  applyFilters: () => void;
  clearFilters: () => void;
  removeFilter: (newFilters: FilterState) => void;
  
  // URL utilities
  getCurrentSearchParams: () => string;
}

/**
 * Hook for managing invoice list URL state
 */
export function useInvoiceListUrlState(): UseInvoiceListUrlStateResult {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Parse initial state from URL
  const getInitialState = useCallback(() => {
    const page = searchParams.get('page');
    const perPage = searchParams.get('perPage');
    const sort = searchParams.get('sort');
    const dir = searchParams.get('dir');
    
    const urlFilters = parseFiltersFromUrl(searchParams);
    const customer = parseCustomerFromUrl(searchParams);
    
    const initialFilterState: FilterState = {
      ...initialFilters,
      ...urlFilters,
      customer,
    };
    
    return {
      page: page ? parseInt(page, 10) : 1,
      pageSize: perPage ? parseInt(perPage, 10) : DEFAULT_PAGE_SIZE,
      sortColumn: (sort && isValidSortColumn(sort) ? sort : 'id') as SortColumn,
      sortDirection: (dir && isValidSortDirection(dir) ? dir : 'desc') as SortDirection,
      filters: initialFilterState,
      isFilterExpanded: (
        customer !== null ||
        initialFilterState.status !== 'all' ||
        initialFilterState.dateFrom !== '' ||
        initialFilterState.dateTo !== '' ||
        initialFilterState.deadlineFrom !== '' ||
        initialFilterState.deadlineTo !== ''
      ),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount
  
  const initial = getInitialState();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(initial.page);
  const [pageSize, setPageSizeState] = useState(initial.pageSize);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>(initial.sortColumn);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initial.sortDirection);
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>(initial.filters);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(initial.filters);
  const [isFilterExpanded, setIsFilterExpanded] = useState(initial.isFilterExpanded);
  
  /**
   * Handle page size change (resets to page 1)
   */
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setCurrentPage(1);
  }, []);
  
  /**
   * Set totals from API response
   */
  const setTotals = useCallback((pages: number, count: number) => {
    setTotalPages(pages);
    setTotalCount(count);
  }, []);
  
  /**
   * Handle column sorting
   */
  const handleSort = useCallback((column: SortColumn) => {
    setCurrentPage(1);
    
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);
  
  /**
   * Apply filters (copy working filters to applied)
   */
  const applyFilters = useCallback(() => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  }, [filters]);
  
  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setCurrentPage(1);
  }, []);
  
  /**
   * Remove a specific filter
   */
  const removeFilter = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    setCurrentPage(1);
  }, []);
  
  /**
   * Get current search params as string
   */
  const getCurrentSearchParams = useCallback(() => {
    return buildSearchParams(currentPage, pageSize, sortColumn, sortDirection, appliedFilters).toString();
  }, [currentPage, pageSize, sortColumn, sortDirection, appliedFilters]);
  
  // Sync URL with state changes
  useEffect(() => {
    const newParams = buildSearchParams(currentPage, pageSize, sortColumn, sortDirection, appliedFilters);
    const currentParamsStr = searchParams.toString();
    const newParamsStr = newParams.toString();
    
    if (currentParamsStr !== newParamsStr) {
      setSearchParams(newParams, { replace: true });
    }
  }, [currentPage, pageSize, sortColumn, sortDirection, appliedFilters, searchParams, setSearchParams]);
  
  return useMemo(() => ({
    // Pagination
    currentPage,
    pageSize,
    totalPages,
    totalCount,
    setCurrentPage,
    setPageSize,
    setTotals,
    
    // Sorting
    sortColumn,
    sortDirection,
    handleSort,
    
    // Filters
    filters,
    appliedFilters,
    isFilterExpanded,
    setFilters,
    setIsFilterExpanded,
    applyFilters,
    clearFilters,
    removeFilter,
    
    // URL utilities
    getCurrentSearchParams,
  }), [
    currentPage,
    pageSize,
    totalPages,
    totalCount,
    setPageSize,
    setTotals,
    sortColumn,
    sortDirection,
    handleSort,
    filters,
    appliedFilters,
    isFilterExpanded,
    applyFilters,
    clearFilters,
    removeFilter,
    getCurrentSearchParams,
  ]);
}

export default useInvoiceListUrlState;
